import styled from "@emotion/styled";
import ico_close from "@src/assets/ico_close.png";
import { useCorpDeungiSubmit } from "@src/hooks/deungi/useDeungiSubmit";
import { useCorpTicketCount } from "@src/hooks/deungi/useTicketCount";
import dayjs from "dayjs";
import Lottie from "lottie-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CORP_DEUNGI_POPUP_TYPE } from "./common/enum";

import _ from "lodash";
import { CorpPopupDto, CorpSearchListDto } from "@src/hooks/deungi/dto/SearchList.dto";

type DeungiPopupPropsType = {
    type: CORP_DEUNGI_POPUP_TYPE;
    toggle: boolean;
    setToggle: (flag: boolean) => void;
    data?: CorpSearchListDto;
    hasOpenData?: CorpPopupDto;
    onClose?: () => void;
};

const CorpDeungiPopup = ({ type, toggle, setToggle, data, hasOpenData, onClose }: DeungiPopupPropsType) => {
    const navigate = useNavigate();
    const [isValid, setIsValid] = useState(true);
    const [popupType, setPopupType] = useState<CORP_DEUNGI_POPUP_TYPE>(type);
    const [ticketCount, setTicketCount] = useState(0);

    const getTicketCount = async () => {
        const res = await useCorpTicketCount();
        setTicketCount(res.curHaveCnt);
    };

    const submitDeungi = async (params: CorpSearchListDto) => {
        const res = await useCorpDeungiSubmit(params);
        const id: string = res.data._id;
        return id;
    };

    const onOkHandler = () => {
        onClose?.();
        setToggle(false);
    };

    const onNextHandler = () => {
        getTicketCount();
        setPopupType(CORP_DEUNGI_POPUP_TYPE.OPEN);
    };

    const onOpenHandler = async () => {
        if (isValid) {
            setIsValid(false);

            if (ticketCount > 0 && data) {
                const id = await submitDeungi(data);

                if (id) {
                    navigate({
                        pathname: "/corpDeungi",
                        search: `?type=search&watch=${id}`,
                    });
                }
            } else {
                localStorage.setItem("corpSelected", JSON.stringify(data));
                navigate("/corpDeungi/payment?inflow=1", { replace: false });
            }
            setToggle(false);
            setIsValid(true);
        }
    };

    const onReopenHandler = (id?: string) => {
        navigate("/corpDeungi/pdf", {
            state: {
                data: id,
            },
        });
        setToggle(false);
    };

    useEffect(() => {
        setPopupType(type);
        if (toggle && type === CORP_DEUNGI_POPUP_TYPE.OPEN) getTicketCount();
    }, [toggle]);

    if (!toggle) return <></>;

    switch (popupType) {
        case CORP_DEUNGI_POPUP_TYPE.OPEN:
            return (
                <Container>
                    <div>
                        <h4>해당 등기를 열람하시겠어요?</h4>
                        <div className="info">내 법인 등기 열람권 : {ticketCount}장</div>
                        <div className="contents">※ 등기 열람권이 없는 경우 충전 페이지로 이동합니다</div>
                        <div className="inputBox">
                            <input className="gray" type="button" value="취소" onClick={onOkHandler} />
                            <input type="button" value="확인" onClick={onOpenHandler} />
                        </div>
                    </div>
                </Container>
            );

        case CORP_DEUNGI_POPUP_TYPE.ERROR:
            return (
                <Container>
                    <div>
                        <h4>
                            검색에 실패하였습니다.
                            <br />
                            다시 시도해주세요.
                        </h4>
                        <input type="button" value="확인" onClick={onOkHandler} />
                    </div>
                </Container>
            );
        case CORP_DEUNGI_POPUP_TYPE.HAS_DEUNGI:
            return (
                <Container>
                    <div>
                        <div className="exitBtnBox" onClick={onOkHandler}>
                            <img src={ico_close} />
                        </div>
                        <h5>열람한 등기가 있어요</h5>
                        <div className="contents-exist">
                            <div className="titleBox">
                                <label>소재지</label>
                                <span>{data?.corporationName}</span>
                            </div>
                            <div className="titleBox">
                                <label>폐쇄여부</label>
                                <span>{data?.deungiStatus}</span>
                            </div>
                            <div className="titleBox">
                                <label>열람일</label>
                                <span>{dayjs(hasOpenData?.createdAt).format("YY.MM.DD")}</span>
                            </div>
                        </div>
                        <div className="inputBox">
                            <input className="gray" type="button" value="신규 열람하기" onClick={onNextHandler} />
                            <input
                                type="button"
                                value="재열람하기"
                                onClick={() => {
                                    onReopenHandler(hasOpenData?._id);
                                }}
                            />
                        </div>
                    </div>
                </Container>
            );
        case CORP_DEUNGI_POPUP_TYPE.INFO:
            return (
                <Container>
                    <div>
                        <h4>실시간 법인 등기 열람권</h4>
                        <div className="contents">
                            전국 법인 등기를
                            <br />
                            실시간으로 열람하실 수 있습니다.
                        </div>
                        <input type="button" value="확인" onClick={onOkHandler} />
                    </div>
                </Container>
            );
    }
};

const Container = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    height: calc(var(--vh, 1dvh) * 100);
    background: rgba(0, 0, 0, 0.5);
    animation: fadeIn 0.3s;
    z-index: 1000;

    & > div {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 24px;
        right: 24px;
        margin: auto;
        height: fit-content;
        color: #0c002c;
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
        padding: 14px;

        h4 {
            font-weight: 500;
            font-size: 16px;
            text-align: center;
            line-height: 1.6;
        }
        h5 {
            font-weight: 500;
            font-size: 16px;
            text-align: center;
            line-height: 2;
            margin: 20px 0;
        }
        ul {
            margin: 5px 0;
            line-height: 1.7;
            padding: 0 14px;

            li {
                font-size: 14px;
                list-style: disc;
                word-break: break-all;
            }
        }
        .exitBtnBox {
            position: absolute;
            top: 0;
            right: 0;
            padding: 18px;

            & > img {
                width: 14px;
                height: 14px;
            }
        }
        .warning {
            font-size: 14px;
            text-align: center;
            line-height: 1.8;
            margin-top: 10px;
            color: #ff0000;
        }
        .info {
            font-size: 14px;
            text-align: center;
            line-height: 1.8;
            margin-top: 10px;
            color: #2275ee;
        }
        .contents {
            font-size: 14px;
            color: #0c002c;
            line-height: 1.6;
            margin: 10px 0;
            text-align: center;

            & > label {
                display: block;
                font-size: 14px;
                color: #8d8d8d;
                margin: 10px 0;
            }
        }
        .titleBox {
            word-break: keep-all;
            & > label {
                display: block;
                color: #8d8d8d;
            }
            & > span {
                display: block;
                margin: 3px 0 15px;
            }
        }
        .contents-exist {
            font-size: 14px;
            color: #0c002c;
            line-height: 1.6;
            margin: 10px 0;
            text-align: left;
        }
        .inputBox {
            display: flex;
            justify-content: space-between;
            column-gap: 10px;
        }
        input {
            width: -webkit-fill-available;
            height: 55px;
            background: #2275ee;
            border: none;
            border-radius: 14px;
            color: #fff;
            font-size: 18px;
            margin-top: 15px;
            padding: 0 10px;

            &.gray {
                background: #ebebeb;
                color: #0c002c;
            }
            &.blue {
                background: #0047ff;
            }
        }
        .alarmListBox {
            max-height: calc(100dvh - 300px);
            overflow: scroll;

            & > table {
                border-collapse: separate;
                width: 100%;
                font-size: 11px;

                & > thead {
                    position: sticky;
                    top: 0;
                }
                & > thead > tr {
                    background: #fbf8f8;
                    color: #505050;

                    & > th {
                        word-break: keep-all;
                        font-weight: 500;
                        border-top: 1px solid #c9c9c9;
                        border-bottom: 1px solid #c9c9c9;
                        border-right: 1px solid #c9c9c9;
                        padding: 7px 0;
                    }
                    & > th:nth-of-type(1) {
                        border-left: 1px solid #c9c9c9;
                        border-radius: 4px 0 0 0;
                    }
                    & > th:nth-of-type(3) {
                        border-radius: 0 4px 0 0;
                    }
                }
                & > tbody > tr {
                    color: #0c002c;
                    animation: fadeIn 0.5s;

                    & > td {
                        word-break: keep-all;
                        font-weight: 500;
                        line-height: 1.4;
                        vertical-align: middle;
                        text-align: center;
                        padding: 7px 3px;
                        border-bottom: 1px solid #c9c9c9;
                        border-right: 1px solid #c9c9c9;
                    }
                    & > td:nth-of-type(1) {
                        border-left: 1px solid #c9c9c9;
                    }
                }
                & > tbody > tr:nth-last-of-type(1) {
                    & > td:nth-of-type(1) {
                        border-radius: 0 0 0 4px;
                    }
                    & > td:nth-last-of-type(1) {
                        border-radius: 0 0 4px 0;
                    }
                    .noList {
                        color: #8d8d8d;
                        border-radius: 0 0 4px 4px !important;
                    }
                }
            }
        }
    }
    .bottomPopup {
        top: auto;
        bottom: 0;
        left: 0;
        right: 0;
        border-radius: 14px 14px 0 0;
        padding: 24px 0 14px;
        animation: slideUp 0.3s ease;
        margin: auto;

        .inputBox {
            display: block;
        }
        .exitBtnBox {
            top: 12px;
        }
        & > h4 {
            font-size: 18px;
            font-weight: bold;
        }
        .titleBox {
            background: #f6faff;
            margin-top: 10px;
            padding: 14px 14px;

            & > label {
                font-size: 16px;
                color: #0c002c;
            }
            & > span {
                margin: 10px 0 0;
                font-size: 14px;
                line-height: 1.6;
                color: #3c3c3c;
            }
        }
        .contents {
            margin: 0 14px 0;
            font-size: 16px;

            .lottie {
                width: 120px;
                height: 120px;
                margin: 6px auto 12px;
            }
        }
        .contents-exist {
            .selectBox {
                display: flex;
                align-items: center;
                box-sizing: border-box;
                border: 1px solid #8d8d8d;
                border-radius: 14px;
                padding: 14px;
                margin: 15px 14px 0;

                &.active {
                    border: 1px solid #0047ff;
                    box-shadow: inset 0 0 0 1px #0047ff;
                }
                & > input {
                    width: 15px;
                    height: 15px;
                    margin: 0 14px 0 0;
                }
                & > div {
                    & > h3 {
                        display: flex;
                        align-items: center;
                        font-size: 18px;

                        & > small {
                            font-size: 14px;
                            font-weight: 500;
                            background: #dff0ff;
                            border-radius: 7px;
                            color: #0c002c;
                            padding: 2px 4px;
                            margin-left: 6px;
                        }
                    }
                    & > label {
                        font-size: 13px;
                        color: #8d8d8d;
                        & > span {
                            color: #ff0000;
                        }
                    }
                }
            }
        }
        input {
            margin: 12px 14px;

            &.gray-2 {
                background: #c9c9c9;
                color: #fff;
            }
            &.blue {
                background: #0047ff;
                font-weight: bold;
            }
        }
    }
    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
    @keyframes slideUp {
        from {
            bottom: -500px;
        }
        to {
            bottom: 0;
        }
    }
`;
export default CorpDeungiPopup;
